import React, { useEffect, useState } from "react";
import { PlusCircle } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import productApis from "../apiProvider/product";
import ReactTableComponent from "../table/ReactTableComponent";
import { Icon } from "@iconify/react/dist/iconify.js";
import usePermission from "../hook/usePermission";
import { useMemo } from "react";
import Swal from "sweetalert2";

export default function TaxListLayer() {
  const canAdd = usePermission("taxList", "add");
  const canEdit = usePermission("taxList", "edit");
  const canDelete = usePermission("taxList", "delete");
  const canView = usePermission("taxList", "view");
  const havePermissions = canView || canEdit || canDelete;

  const [taxes, setTaxes] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [errors, setErrors] = useState({});
  const [viewTax, setViewTax] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "Percentage",
    rate: "",
    status: "",
  });

  useEffect(() => {
    fetchData();
  }, [pageIndex]);

  const fetchData = async () => {
    const productResult = await productApis.getTax();
    if (productResult.status) {
      const all = productResult.response.data;
      setTotal(all.length);
      const start = pageIndex * limit;
      setTaxes(all.slice(start, start + limit));
    }
  };

  const handleView = (tax) => {
    setViewTax({
      name: tax.taxName,
      type: tax.taxType,
      rate: tax.taxRate + "%",
      status: tax.isActive ? "Active" : "Inactive",
    });
  };

  const handleEdit = (tax) => {
    setEditId(tax._id);
    setFormData({
      name: tax.taxName ?? "",
      type: tax.taxType || "Percentage",
      rate: tax.taxRate ?? "", // <-- preserves 0
      status: tax.isActive ? "Active" : "Inactive",
    });
    setEditModalVisible(true);
  };

  const handleDelete = async (tax) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this tax?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const productResult = await productApis.deleteTax(tax._id);
          if (productResult.status) {
            Swal.fire("Deleted!", "Tax has been deleted.", "success");
            toast.success("Tax deleted successfully");
            fetchData();
          } else {
            Swal.fire("Error!", "Failed to delete tax.", "error");
            toast.error("Failed to delete tax");
          }
        } catch (error) {
          console.error("Delete failed:", error);
          Swal.fire("Error!", "Something went wrong.", "error");
          toast.error("Something went wrong");
        }
      }
    });
  };

  const handleCloseModals = () => {
    setEditModalVisible(false);
    setViewTax(null);
    setEditId(null);
    setFormData({
      name: "",
      type: "Percentage",
      rate: "",
      status: "",
    });
  };

  const handleChange = (e) => {
    setErrors({});
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSubmit = async () => {
    const validationErrors = validateForm();
    setErrors(validationErrors); // ⬅️ update state

    if (Object.keys(validationErrors).length > 0) {
      return; // stop submit
    }

    const input = {
      taxName: formData.name.trim(),
      taxType: formData.type,
      taxRate: Number(formData.rate),
      isActive: formData.status === "Active",
    };

    let productResult;
    if (editId) {
      productResult = await productApis.updateTax(input, editId);
    } else {
      productResult = await productApis.createTax(input);
    }

    if (productResult.status) {
      toast.success(
        editId ? "Tax Updated Successfully" : "Tax Created Successfully"
      );
      handleCloseModals();
      fetchData();
    } else {
      toast.error(productResult.message || "Something went wrong. Please try again.");
    }
  };

  const handleNextPage = () => {
    if ((pageIndex + 1) * limit < total) {
      setPageIndex((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageIndex > 0) {
      setPageIndex((prev) => prev - 1);
    }
  };

  const columns = useMemo(() => [
    {
      header: "S.No",
      accessorKey: "index",
      cell: ({ row }) => pageIndex * limit + row.index + 1,
    },
    { header: "Tax Name", accessorKey: "taxName" },
    { header: "Tax Type", accessorKey: "taxType" },
    {
      header: "Tax Rate (%)",
      accessorKey: "taxRate",
      cell: ({ row }) => `${row.original.taxRate}%`,
    },
    {
      header: "Status",
      accessorKey: "isActive",
      cell: ({ row }) => (
        <button
          className={`btn btn-subtle-${row.original.isActive ? "success" : "danger"
            } btn-sm`}
        >
          {row.original.isActive ? "Active" : "Inactive"}
        </button>
      ),
    },
    ...(havePermissions
      ? [
        {
          header: "Actions",
          size: 160,
          cell: (info) => {
            const childCategory = info.row.original;
            return (
              <div className="d-flex justify-content-start align-items-center gap-2">
                {/* {canView && (
                  <button
                    type="button"
                    className="d-flex justify-content-center align-items-center bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px rounded-circle"
                    onClick={() => handleView(childCategory)}
                  >
                    <Icon icon="majesticons:eye-line" className="text-xl" />
                  </button>
                )} */}
                {canEdit && (
                  <button
                    type="button"
                    className="d-flex justify-content-center align-items-center bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px rounded-circle"
                    onClick={() => handleEdit(childCategory)}
                  >
                    <Icon icon="lucide:edit" />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="d-flex justify-content-center align-items-center bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px rounded-circle"
                    onClick={() => handleDelete(childCategory)}
                  >
                    <Icon icon="fluent:delete-24-regular" />
                  </button>
                )}
              </div>
            );
          },
        },
      ]
      : []),
  ], [pageIndex, limit, canEdit, canDelete, canView, havePermissions]);
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Tax Name is required";
    }


    if (
      formData.rate === "" ||
      formData.rate === null ||
      isNaN(formData.rate)
    ) {
      errors.rate = "Tax Rate is required";
    } else if (Number(formData.rate) < 0) {
      errors.rate = "Tax Rate must be a positive number";
    }

    if (!formData.status) {
      errors.status = "Status is required";
    }

    return errors;
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            {canAdd && (
              <div className="d-flex justify-content-end align-items-center">
                <button
                  className="btn btn-success d-inline-flex align-items-center fw-bold"
                  style={{ fontSize: "16px" }}
                  onClick={() => {
                    setEditId(null);
                    setFormData({ name: "", type: "Percentage", rate: "", status: "" });
                    setEditModalVisible(true);
                  }}
                >
                  <PlusCircle size={20} weight="fill" className="me-2" />
                  Create Tax
                </button>
              </div>
            )}

            <ReactTableComponent
              data={taxes}
              columns={columns}
              pageIndex={pageIndex}
              totalPages={Math.ceil(total / limit)}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              totalRecords={total}
            />

            {/* View Modal */}
            {viewTax && !editModalVisible && (
              <div
                className="modal fade show d-block"
                role="dialog"
                style={{ backgroundColor: "rgb(0 0 0 / 60%)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Tax Details</h5>
                      {/* <button type="button" className="btn-close" onClick={handleCloseModals}></button> */}
                    </div>
                    <div className="modal-body p-4">
                      {Object.entries(viewTax).map(([label, value], idx) => (
                        <div className="mb-3 row" key={idx}>
                          <div className="col-md-6">
                            <strong>
                              {label.charAt(0).toUpperCase() + label.slice(1)}:
                            </strong>
                          </div>
                          <div className="col-md-6">{value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={handleCloseModals}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Create/Edit Modal */}
            {editModalVisible && (
              <div className="modal fade show d-block" role="dialog" style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}>
                <div className="modal-dialog modal-dialog modal-dialog-centered">
                  <div className="modal-content radius-16 bg-base">
                    <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
                      <p className="modal-title fs-5" style={{ color: "#000", fontWeight: 600 }}>
                        {editId ? "Edit Tax" : "Create Tax"}
                      </p>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCloseModals}
                      ></button>
                    </div>
                    <div className="modal-body p-4">
                      <form>
                        <div className="row">
                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Tax Name <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              className="form-control"
                            />
                            {errors.name && (
                              <p className="text-danger">{errors.name}</p>
                            )}
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Tax Type <span className="text-danger">*</span></label>
                            <div className="d-flex align-items-center bg-light px-16 py-10 rounded-8 w-100 border border-secondary-200" style={{ height: "45px" }}>
                              <Icon icon="material-symbols:percent" className="me-2 text-success-600 text-xl" />
                              <span className="fw-semibold text-secondary-700">Percentage</span>
                            </div>
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Tax Rate <span className="text-danger">*</span></label>
                            <input
                              type="number"
                              name="rate"
                              value={formData.rate}
                              onChange={(e) => {
                                setErrors({});
                                const value = e.target.value;
                                if (value === "" || Number(value) >= 0) {
                                  setFormData({ ...formData, rate: value });
                                }
                              }}
                              className="form-control"
                            // min="0"
                            />
                            {errors.rate && (
                              <p className="text-danger">{errors.rate}</p>
                            )}
                          </div>
                          <div className="col-lg-6 mb-3">
                            <label className="form-label">Status <span className="text-danger">*</span></label>
                            <select
                              name="status"
                              value={formData.status}
                              onChange={handleChange}
                              className="form-select"
                            >
                              <option value="">Select</option>
                              <option value="Active">Active</option>
                              <option value="Inactive">Inactive</option>
                            </select>
                            {errors.status && (
                              <p className="text-danger">{errors.status}</p>
                            )}
                          </div>
                        </div>
                      </form>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-success radius-8 px-32"
                        style={{ height: "45px" }}
                        onClick={handleSubmit}
                      >
                        {editId ? "Update" : "Save"}
                      </button>
                      <button
                        className="btn btn-secondary radius-8 px-32 ms-2"
                        style={{ height: "45px" }}
                        onClick={handleCloseModals}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
